import { Alert, Box, Button, CircularProgress, Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client/react';
import { Batch, CombinedCalculatedItemResult } from '../../models/Batch';
import { ProductItem } from '../../models/ProductItem';
import { GET_PROCESS_BATCHES_RESULT_QUERY } from '../../queries/BatchQueries';
import {
  DataGrid,
  GridColDef,
  GridRowClassNameParams,
  GridRowId,
  gridClasses,
} from '@mui/x-data-grid';

const INITIAL_PAGE_SIZE = 100;

export default function BatchProcess() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [getBatchesForProcessingByIds] = useLazyQuery(
    GET_PROCESS_BATCHES_RESULT_QUERY);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [combinedMatchingBatch, setCombinedMatchingBatch] = useState<Batch | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<GridRowId>>(new Set<GridRowId>());

  const [rowsState, setRowsState] = useState<{
    rows: CombinedCalculatedItemResult[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const loadData = useCallback(async () => {
    setError(null);

    const unparsedBatchIds = searchParams.get('batchIds') || '';

    const batchIds = unparsedBatchIds.split(',').map(id => parseInt(id, 10));

    if (batchIds.length > 0) {
      setIsLoading(true);

      try {
        const { data, error: getBatchesByIdsError } = await getBatchesForProcessingByIds(
          { variables: { ids: batchIds } }
        );

        setIsLoading(false);
        setError(null);

        if (getBatchesByIdsError) {
          setError(getBatchesByIdsError);
        }

        if (data?.processedBatchesResult) {
          setBatches(data.processedBatchesResult.batches);
          setCombinedMatchingBatch(data.processedBatchesResult.combinedMatchedBatch);

          setRowsState({
            rows: data.processedBatchesResult.combinedCalculatedItemResults || [],
            rowCount: data.processedBatchesResult.combinedCalculatedItemResults.length || 0,
          });
        } else {
          setError({ message: 'No batches found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getBatchesForProcessingByIds, searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/batches');
  }, [navigate]);

  const handleShoppingList = useCallback(() => {
    navigate(`/dashboard/batches/shopping-list?batchIds=${batches.map(batch => batch.id).join(',')}&itemIds=${[...selectedItemIds || []].join(',')}`);
  }, [batches, navigate, selectedItemIds]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'totalQuantity',
        headerName: 'Total Qty (lbs)',
        type: 'number',
        width: 100,
        display: 'flex',
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'itemName',
        headerName: 'Item',
        width: 125,
        display: 'flex',
        valueGetter: (_, row) => row.item?.name || '',
        renderCell: (params) => {
          return <a href={`/dashboard/items/${params.row.item?.id}`}>{params.row.item?.name || 'N/A'}</a>
        }
      },
      {
        field: 'totalQuantityOnHand',
        headerName: 'Total QOH (lbs)',
        type: 'number',
        width: 125,
        display: 'flex',
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'totalRemainingQuantityOnHand',
        headerName: 'ROH (lbs)',
        type: 'number',
        width: 100,
        display: 'flex',
        valueFormatter: (value: number) => value.toFixed(3)
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (params.row.totalRemainingQuantityOnHand <= 0) {
      return 'product-batch-quantity-not-met';
    }

    return '';
  }

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return (batches || []).length > 0 ?
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 7, sm: 7 }}>
            <Grid container spacing={2}>
              {batches.map((batch: Batch) => (
                <Grid key={batch.id} size={{ xs: 12, sm: 6 }}>
                  <Paper sx={{ px: 2, py: 1 }}>
                    <Typography variant="overline" sx={{ fontSize: '125%' }}>
                      <strong>
                        <a target="_blank" rel="noreferrer" href={`/dashboard/products/${batch.product?.id}`}>{batch.product?.name}</a>, {batch.amount}lbs
                      </strong>
                    </Typography>
                    <Table aria-label="dense table" size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row" align="right">Qty (lbs)</TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }} component="th" scope="row">Item</TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody
                        sx={[
                          () => ({
                            '.quantity-not-available': {
                              backgroundColor: '#f8d7c0',
                              '&:hover': {
                                backgroundColor: 'rgba(248,215,192,.75)',
                                filter: 'brightness(105%)'
                              }
                            }
                          }),
                          (theme) =>
                            theme.applyStyles('dark', {
                              '.quantity-not-available': {
                                backgroundColor: '#ed201c',
                                '&:hover': {
                                  backgroundColor: '#ed201c',
                                  filter: 'brightness(120%)'
                                }
                              }
                            })]}>
                        {(batch.calculatedProductItems || []).map((calculatedProductItem: ProductItem) => (
                          <TableRow
                            key={calculatedProductItem.id}
                            className={calculatedProductItem.productBatchQuantityMet ? '' : 'quantity-not-available'}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} align="right">
                              {(calculatedProductItem.productBatchQuantity || 0).toFixed(3)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }}>
                              {calculatedProductItem.item?.name}
                            </TableCell>
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                              {calculatedProductItem.notes}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} align="right">
                            {(batch.calculatedProductItems || []).map(calculatedProductItem => calculatedProductItem.productBatchQuantity || 0).reduce((a, b) => a + b, 0).toFixed(3)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }}>
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid size={{ xs: 5, sm: 5 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline" sx={{ fontSize: '125%' }}>
                <strong>Item Totals</strong>
              </Typography>
              <Box sx={{ height: '500px', width: '100%' }}>
                <DataGrid
                  density="compact"
                  checkboxSelection
                  rows={rowsState.rows}
                  columns={columns}
                  loading={isLoading}
                  getRowId={(row: CombinedCalculatedItemResult) => row.item?.id || ''}
                  pageSizeOptions={[INITIAL_PAGE_SIZE]}
                  getRowHeight={() => 'auto'}
                  getRowClassName={getRowClassName}
                  onRowSelectionModelChange={(newSelectionModel) => {
                    if (newSelectionModel.type === 'exclude') {
                      if (newSelectionModel.ids.size === 0) {
                        setSelectedItemIds(
                          new Set((rowsState.rows || []).map(combinedItemResult => combinedItemResult.item.id)));
                      } else {
                        const updatedSelectedBatches = rowsState.rows.filter(
                          (row) => !newSelectionModel.ids.has(row.item.id));

                        setSelectedItemIds(
                          new Set(updatedSelectedBatches.map(combinedItemResult => combinedItemResult.item.id)));
                      }
                    } else {
                      setSelectedItemIds(newSelectionModel.ids);
                    }
                  }}
                  sx={[
                    () => ({
                      fontSize: '85%',
                      '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                      '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                      '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                      [`& .${gridClasses.columnHeader}, & .${gridClasses.cell} `]: {
                        outline: 'transparent',
                      },
                      [`& .${gridClasses.columnHeader}: focus - within, & .${gridClasses.cell}: focus - within`]:
                      {
                        outline: 'none',
                      },
                      [`& .${gridClasses.row}: hover`]: {
                        cursor: 'pointer',
                      },
                      '.product-batch-quantity-not-met': {
                        backgroundColor: '#f8d7c0',
                        '&:hover': {
                          backgroundColor: 'rgba(248,215,192,.75)',
                          filter: 'brightness(105%)'
                        }
                      }
                    }),
                    (theme) =>
                      theme.applyStyles('dark', {
                        '.product-batch-quantity-not-met': {
                          backgroundColor: '#ed201c',
                          '&:hover': {
                            backgroundColor: '#ed201c',
                            filter: 'brightness(120%)'
                          }
                        }
                      })
                  ]}
                  slotProps={{
                    loadingOverlay: {
                      variant: 'circular-progress',
                      noRowsVariant: 'circular-progress',
                    },
                    baseIconButton: {
                      size: 'small',
                    },
                  }}
                />
              </Box>
              <Stack direction="row" spacing={2} justifyContent="right" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                  disabled={(selectedItemIds || []).size === 0}
                  onClick={handleShoppingList}
                >
                  Shopping List
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        {combinedMatchingBatch ?
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline" sx={{ fontSize: '125%' }}>
                  <strong>Combined Batches</strong>
                </Typography>
                <Table aria-label="dense table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">Qty (lbs)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }} component="th" scope="row">Item</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(combinedMatchingBatch.calculatedProductItems || []).map(productItem => (
                      <TableRow
                        key={productItem.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                          {productItem.productBatchQuantity?.toFixed(3)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }}>
                          {productItem.product?.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                          {productItem.notes}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                        {(combinedMatchingBatch.calculatedProductItems || []).map(calculatedProductItem => calculatedProductItem.productBatchQuantity || 0).reduce((a, b) => a + b, 0).toFixed(3)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }}>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid> :
          <></>
        }
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Stack>
      </Box> : <></>;
  }, [isLoading, error, batches, rowsState.rows, columns, selectedItemIds, handleShoppingList, combinedMatchingBatch, handleBack]);

  const pageTitle = 'Maths';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Batches', path: '/dashboard/batches' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}